import getEnvValue from 'commons/getEnvValue';

// Product-specific option images (CON-8148) are uploaded through Backbone and
// stored on the public image host as-is - no additional sizes are generated,
// because the field is primarily used for SVG assets which scale to any
// resolution. They are therefore NOT served through imgix, and must not be
// rendered with JBImage/react-imgix.
const DEFAULT_PRODUCT_OPTION_IMAGE_BASE_URL = 'https://jb-img.co/public_img';

// Uploads land in the `option_images` folder of the public image root, verified
// against existing values (e.g. `1006+-CL043.jpg` resolves at
// https://jb-img.co/public_img/option_images/1006+-CL043.jpg). This is storage
// layout rather than host configuration, so it is not part of the env var.
const PRODUCT_OPTION_IMAGE_FOLDER = 'option_images';

const isAbsoluteUrl = path => /^(https?:)?\/\//i.test(path);

// Uploaded filenames are free-form and routinely contain spaces (e.g.
// "170 - Orientation Configuration - 2 Recliner.svg"), so every segment has to
// be percent-encoded. Decoding first keeps this idempotent for values that were
// already encoded, and falls back to the raw segment when the value contains a
// stray "%" that is not a valid escape.
const encodePathSegment = segment => {
  let decoded = segment;

  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // Not valid percent-encoding - encode the segment as it was given
  }

  return encodeURIComponent(decoded);
};

/**
 * Build the full URL for a `product_variants_options.product_option_image`
 * value. The column stores a bare filename following the
 * `{product_id}+{option_value_sku}.{extension}` convention (e.g.
 * "1006+-CL043.jpg"), but any path - or a full URL - is supported.
 *
 * Returns null when the product has no image of its own, so callers can fall
 * back to the global option value image asset.
 *
 * @param {string|null|undefined} productOptionImage
 * @returns {string|null}
 */
const getProductOptionImageUrl = productOptionImage => {
  if (typeof productOptionImage !== 'string') {
    return null;
  }

  const path = productOptionImage.trim();

  // Backbone exports unset values as the literal string "null"
  if (!path || path.toLowerCase() === 'null') {
    return null;
  }

  if (isAbsoluteUrl(path)) {
    return path;
  }

  const baseUrl = (
    getEnvValue('NEXT_PUBLIC_PRODUCT_OPTION_IMAGE_BASE_URL') ||
    DEFAULT_PRODUCT_OPTION_IMAGE_BASE_URL
  ).replace(/\/+$/, '');

  const encodedPath = path
    .replace(/^\/+/, '')
    .split('/')
    .map(encodePathSegment)
    .join('/');

  return `${baseUrl}/${PRODUCT_OPTION_IMAGE_FOLDER}/${encodedPath}`;
};

export default getProductOptionImageUrl;
